import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Loading } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Wallet, UserPlus, Trash2, CalendarClock, DollarSign, GraduationCap, Users2, MessageSquare, KeyRound } from "lucide-react";

const DURATIONS = [
  { label: "1 month", days: 30 },
  { label: "3 months", days: 90 },
  { label: "6 months", days: 180 },
  { label: "12 months", days: 365 },
];
const today = () => new Date().toISOString().slice(0, 10);

const STATUS = {
  active: { label: "Active", cls: "bg-[#ECFDF5] text-[#059669]" },
  due_soon: { label: "Due soon", cls: "bg-[#FFFBEB] text-[#B45309]" },
  overdue: { label: "Overdue", cls: "bg-[#FEF2F2] text-[#E11D48]" },
  none: { label: "No record", cls: "bg-[#F3F4F6] text-[#6B7280]" },
};

function StatusBadge({ s, days }) {
  const cfg = STATUS[s] || STATUS.none;
  let extra = "";
  if (s === "overdue") extra = ` · ${Math.abs(days)}d`;
  else if (s === "active" || s === "due_soon") extra = ` · ${days}d left`;
  return <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${cfg.cls}`}>{cfg.label}{extra}</span>;
}

export default function Payments() {
  const [groups, setGroups] = useState(null);
  const [programs, setPrograms] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [payFor, setPayFor] = useState(null); // student row
  const [parentFor, setParentFor] = useState(null); // student row
  const [resetFor, setResetFor] = useState(null); // student row
  const [delFor, setDelFor] = useState(null);

  const load = () => api.get("/tutor/payments").then((r) => setGroups(r.data));
  useEffect(() => { load(); api.get("/programs").then((r) => setPrograms(r.data)); }, []);
  if (!groups) return <Loading />;

  const totalStudents = groups.reduce((n, g) => n + g.students.length, 0);
  const overdue = groups.reduce((n, g) => n + g.students.filter((s) => s.status === "overdue").length, 0);

  return (
    <div className="animate-fade-up" data-testid="payments-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Payments" subtitle="Track fees by class, record payments and get reminded when families are due to pay again." />
        <Button onClick={() => setAddOpen(true)} data-testid="add-student-btn" className="h-11 rounded-xl ember-gradient font-bold"><UserPlus className="mr-1.5 h-4 w-4" /> Add student</Button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="ember-card p-4"><div className="text-xs font-semibold text-[#948EAB]">Students</div><div className="mt-1 font-heading text-2xl font-extrabold text-[#1E1B2E]">{totalStudents}</div></div>
        <div className="ember-card p-4"><div className="text-xs font-semibold text-[#948EAB]">Classes</div><div className="mt-1 font-heading text-2xl font-extrabold text-[#1E1B2E]">{groups.length}</div></div>
        <div className="ember-card p-4"><div className="text-xs font-semibold text-[#948EAB]">Overdue</div><div className={`mt-1 font-heading text-2xl font-extrabold ${overdue ? "text-[#E11D48]" : "text-[#059669]"}`}>{overdue}</div></div>
      </div>

      <div className="space-y-8">
        {groups.map((g) => (
          <div key={g.program} data-testid={`pay-class-${g.program}`}>
            <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-extrabold text-[#1E1B2E]">
              <GraduationCap className="h-5 w-5 text-[#7C3AED]" /> {g.program_name}
              <span className="text-sm font-semibold text-[#948EAB]">({g.students.length})</span>
            </h2>
            <div className="grid gap-3">
              {g.students.map((s) => (
                <div key={s.id} className="ember-card flex flex-wrap items-center gap-4 p-5" data-testid={`pay-row-${s.id}`}>
                  <Avatar className="h-11 w-11"><AvatarImage src={s.avatar_url} /><AvatarFallback className="bg-[#EDE9FE] text-[#5B21B6]">{s.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold text-[#1E1B2E]">{s.name}</div>
                    <div className="truncate text-xs text-[#948EAB]">{s.email}</div>
                  </div>
                  <div className="hidden text-right sm:block">
                    <div className="text-sm font-bold text-[#1E1B2E]">{s.amount != null ? `$${Number(s.amount).toFixed(0)}` : "—"}</div>
                    <div className="text-xs text-[#948EAB]">{s.paid_date ? `Paid ${s.paid_date}` : "No payment yet"}</div>
                  </div>
                  <div className="hidden text-right md:block">
                    <div className="text-xs font-semibold text-[#524B6B]">{s.next_due ? `Due ${s.next_due}` : "—"}</div>
                    <div className="text-xs text-[#948EAB]">{s.duration_days ? `${s.duration_days}d cycle` : ""}</div>
                  </div>
                  <StatusBadge s={s.status} days={s.days_left} />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid={`record-payment-${s.id}`} onClick={() => setPayFor(s)} className="h-9 rounded-xl text-xs font-bold"><DollarSign className="mr-1 h-3.5 w-3.5" /> Payment</Button>
                    <Button size="sm" variant="outline" data-testid={`parent-${s.id}`} onClick={() => setParentFor(s)} className={`h-9 rounded-xl text-xs font-bold ${s.parent_phone ? "text-[#059669]" : ""}`}><Users2 className="mr-1 h-3.5 w-3.5" /> Parent</Button>
                    <Button size="sm" variant="outline" data-testid={`reset-password-${s.id}`} onClick={() => setResetFor(s)} className="h-9 rounded-xl text-xs font-bold"><KeyRound className="mr-1 h-3.5 w-3.5" /> Password</Button>
                    <button data-testid={`delete-student-${s.id}`} onClick={() => setDelFor(s)} className="rounded-lg p-2 text-[#E11D48] hover:bg-[#FFF1F2]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              {g.students.length === 0 && <div className="ember-card py-8 text-center text-sm text-[#948EAB]">No students in this class.</div>}
            </div>
          </div>
        ))}
        {totalStudents === 0 && <div className="ember-card py-12 text-center text-sm text-[#948EAB]">No students yet. Add your first student to start tracking payments.</div>}
      </div>

      {addOpen && <AddStudentDialog programs={programs} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); }} />}
      {payFor && <PaymentDialog student={payFor} onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); load(); }} />}
      {parentFor && <ParentDialog student={parentFor} onClose={() => setParentFor(null)} onDone={() => { setParentFor(null); load(); }} />}
      {resetFor && <ResetPasswordDialog student={resetFor} onClose={() => setResetFor(null)} />}

      <AlertDialog open={!!delFor} onOpenChange={(o) => !o && setDelFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delFor?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the student account and all their exam attempts. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-student" className="bg-[#E11D48] hover:bg-[#be123c]" onClick={async () => {
              try { await api.delete(`/tutor/students/${delFor.id}`); toast.success(`${delFor.name} deleted`); setDelFor(null); load(); }
              catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
            }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ResetPasswordDialog({ student, onClose }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (pwd.length < 6) return toast.error("Password must be at least 6 characters.");
    setBusy(true);
    try {
      await api.post(`/tutor/students/${student.id}/reset-password`, { new_password: pwd });
      toast.success(`New password set for ${student.name}.`);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not reset password."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#7C3AED]" /> Reset password</DialogTitle></DialogHeader>
        <p className="text-sm text-[#524B6B]">Set a new password for <b>{student.name}</b> ({student.email}). Share it with them securely — they can change it later from their own account.</p>
        <div className="py-2"><Label>New password</Label><Input data-testid="reset-password-input" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="At least 6 characters" className="mt-1.5 h-11 rounded-xl" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-reset-password" className="rounded-xl ember-gradient font-bold">{busy ? "Saving…" : "Set password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddStudentDialog({ programs, onClose, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [program, setProgram] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/tutor/students", { name: name.trim(), email: email.trim(), password, program });
      toast.success("Student account created");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-student-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#7C3AED]" /> Add student</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Full name</Label><Input data-testid="new-student-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Lee" className="mt-1.5 h-11 rounded-xl" /></div>
          <div><Label>Email</Label><Input data-testid="new-student-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="student@example.com" className="mt-1.5 h-11 rounded-xl" /></div>
          <div><Label>Temporary password</Label><Input data-testid="new-student-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="mt-1.5 h-11 rounded-xl" /></div>
          <div>
            <Label>Class / stream</Label>
            <Select value={program} onValueChange={setProgram}>
              <SelectTrigger data-testid="new-student-program" className="mt-1.5 h-11 rounded-xl"><SelectValue placeholder="Choose stream" /></SelectTrigger>
              <SelectContent>{Object.values(programs).map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-new-student" className="rounded-xl ember-gradient font-bold">{busy ? "Creating…" : "Create student"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ParentDialog({ student, onClose, onDone }) {
  const [name, setName] = useState(student.parent_name || "");
  const [email, setEmail] = useState(student.parent_email || "");
  const [phone, setPhone] = useState(student.parent_phone || "");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/tutor/students/${student.id}/parent`, { parent_name: name.trim(), parent_email: email.trim(), parent_phone: phone.trim() });
      toast.success("Parent contact saved");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const showPreview = async () => {
    try {
      const { data } = await api.get(`/tutor/students/${student.id}/parent-preview`);
      setPreview(data);
      if (!data.sms_configured) toast.message("Texts are inactive until Twilio keys are added — this is a preview only.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="parent-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users2 className="h-5 w-5 text-[#7C3AED]" /> Parent for {student.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Parent name</Label><Input data-testid="parent-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Parent / guardian name" className="mt-1.5 h-11 rounded-xl" /></div>
          <div><Label>Parent email</Label><Input data-testid="parent-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="parent@example.com" className="mt-1.5 h-11 rounded-xl" /></div>
          <div><Label>Mobile number (for SMS)</Label><Input data-testid="parent-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+61400000000" className="mt-1.5 h-11 rounded-xl" />
            <p className="mt-1 text-xs text-[#948EAB]">Use international format (e.g. +61…). A fortnightly progress text is sent to this number.</p></div>
          <button type="button" onClick={showPreview} data-testid="preview-sms" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline"><MessageSquare className="h-4 w-4" /> Preview the fortnightly text</button>
          {preview && <pre data-testid="sms-preview" className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-xl bg-[#F5F3FF] p-4 text-xs leading-relaxed text-[#1E1B2E]">{preview.message}</pre>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-parent" className="rounded-xl ember-gradient font-bold">{busy ? "Saving…" : "Save parent"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ student, onClose, onDone }) {
  const [amount, setAmount] = useState(student.amount ?? "");
  const [paidDate, setPaidDate] = useState(student.paid_date || today());
  const [duration, setDuration] = useState(String(student.duration_days || 90));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount.");
    setBusy(true);
    try {
      await api.put(`/tutor/students/${student.id}/payment`, { amount: Number(amount), paid_date: paidDate, duration_days: Number(duration) });
      toast.success("Payment recorded");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[#7C3AED]" /> Record payment — {student.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Amount ($)</Label><Input data-testid="payment-amount" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="450" className="mt-1.5 h-11 rounded-xl" /></div>
          <div><Label>Date paid</Label><Input data-testid="payment-date" type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
          <div>
            <Label>Duration (covers next…)</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="payment-duration" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{DURATIONS.map((d) => <SelectItem key={d.days} value={String(d.days)}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mt-2 text-xs text-[#948EAB]">A reminder is created automatically once this period ends so you can ask the family to pay again.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-payment" className="rounded-xl ember-gradient font-bold">{busy ? "Saving…" : "Save payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
